import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useApi } from "@/lib/api/useApi";
import { useMutate } from "@/lib/api/useMutate";
import { queryCache } from "@/lib/api/queryCache";
import { favoriteIdsQuery, favoritesQuery } from "@/lib/api/queries";
import { toggleFavorite } from "@/lib/api/wishlist";
import { Icon } from "@/components/store/Icon";
import { toast } from "sonner";

import type { Product } from "@/lib/api/types";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { resolveAssetUrl } from "@/lib/api/http";
import { useTranslation } from "react-i18next";

export function ProductCard({ product }: { product: Product }) {
  const { t } = useTranslation();
  const { formatPrice } = useFormatters();
  const { productName, productCategory } = useLocalizedContent();

  const { data: favoriteIds = [] } = useApi(favoriteIdsQuery().queryKey, favoriteIdsQuery().queryFn);
  const favoriteMutation = useMutate(
    (productId: string) => toggleFavorite(productId),
    {
      onSuccess: (ids, productId) => {
        queryCache.invalidate(favoriteIdsQuery().queryKey);
        queryCache.invalidate(favoritesQuery().queryKey);
        toast.success(ids.includes(productId) ? t("favorites.added") : t("favorites.removed"));
      },
      onError: (error: Error) => {
        if (error.message.includes("bearer token") || error.message.includes("jwt")) {
          toast.error(t("auth.pleaseLogin", { defaultValue: "Please login first" }));
        } else {
          toast.error(error.message);
        }
      },
    }
  );

  const compareAt = product.compareAtPrice ?? 0;
  const hasDiscount = compareAt > 0 && compareAt > product.price;
  const originalPrice = hasDiscount ? compareAt : product.price;
  const salePrice = product.price;
  const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : 0;
  const isComingSoon = product.status === "coming_soon";
  const isFavorited = favoriteIds.includes(product.id);

  const cardContent = (
    <div className="group flex h-full flex-col relative">
      <div className="relative aspect-square overflow-hidden rounded-lg mb-4 bg-surface-container">
        <div
          className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
          style={{ backgroundImage: `url("${resolveAssetUrl(product.images?.[0])}")` }}
        />
        {hasDiscount && !isComingSoon && (
          <span className="absolute top-3 start-3 bg-red-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm z-10">
            -{discountPercent}%
          </span>
        )}
        {isComingSoon && (
          <span className="absolute top-3 start-3 bg-secondary text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm z-10">
            {t("admin.common.comingSoon", { defaultValue: "Coming Soon" })}
          </span>
        )}

        {/* Favorite Button overlay */}
        <div className="absolute top-3 end-3 z-30">
          <button
            type="button"
            aria-label={t(
              isFavorited ? "common.removeFromFavorites" : "common.addToFavorites",
              { name: productName(product.name) },
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              favoriteMutation.mutate(product.id);
            }}
            className={`w-9 h-9 md:w-10 md:h-10 backdrop-blur rounded-full flex items-center justify-center transition-all luxury-shadow cursor-pointer ${
              isFavorited
                ? "bg-surface-container-lowest/90 text-golden-latte"
                : "bg-surface-container-lowest/85 text-primary hover:bg-secondary hover:text-on-secondary"
            }`}
          >
            <Icon
              name="favorite"
              className="text-[18px]"
              filled={isFavorited}
            />
          </button>
        </div>
      </div>
      <div className="flex flex-1 flex-col space-y-1">
        <p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
          {productCategory(product.category)}
        </p>
        <h3 className="font-body-lg text-body-lg truncate group-hover:text-secondary transition-colors">
          {productName(product.name)}
        </h3>
        <div className="flex flex-col gap-0.5 mt-auto pt-2">
          {isComingSoon ? (
            <span className="font-body-md text-body-md text-on-surface-variant/50 whitespace-nowrap">
              {t("admin.common.comingSoon", { defaultValue: "Coming Soon" })}
            </span>
          ) : hasDiscount ? (
            <>
              <span className="font-body-sm text-body-sm text-red-600 line-through whitespace-nowrap">
                {formatPrice(originalPrice)}
              </span>
              <span className="font-body-md text-body-md text-primary font-bold whitespace-nowrap">
                {formatPrice(salePrice)}
              </span>
            </>
          ) : (
            <span className="font-body-md text-body-md text-on-surface-variant whitespace-nowrap">
              {formatPrice(product.price)}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: "0px 0px -60px 0px" }}
      whileHover={isComingSoon ? undefined : { scale: 1.02 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      className="h-full animate-fade-in"
    >
      {isComingSoon ? (
        <div className="h-full cursor-default opacity-80">
          {cardContent}
        </div>
      ) : (
        <Link to={`/product/${product.slug}`} className="h-full block">
          {cardContent}
        </Link>
      )}
    </motion.div>
  );
}
